/* eslint-disable react/jsx-key */
import React from 'react';

import ModalManager from '../../components/ModalManager';
import { useModalManagerContext } from '../../components/ModalManager/ModalManagerContext';
import ReasonSelectorModal from './ReasonSelectorModal';
import FrequencyEditorStep1Modal from './FrequencyEditorStep1Modal';
import FrequencyEditorStep2Modal from './FrequencyEditorStep2Modal';
import ConfirmCancellationModal from './ConfirmCancellationModal';
import UpdateAddressFormModal from './UpdateAddressFormModal';
import UpdateAddressPromptModal from './UpdateAddressPromptModal';
import UpdateAddressConfirmModal from './UpdateAddressConfirmModal';
import DiscountAvailableModal from './DiscountAvailableModal';
import PausePromptModal from './PausePromptModal';
import SkipPromptModal from './SkipPromptModal';
import RescheduleModal from './RescheduleModal';
import ConfirmPrepaidCancellationModal from './ConfirmPrepaidCancellationModal';
import ConfirmCancelPrepaidMultiOrderModal from './ConfirmCancelPrepaidMultiOrder';
import { useGlobalContext } from '../state/GlobalContext';
import { CancelReasons } from './cancelReasons';

interface Props {
  isBatchedV2?: boolean;
  isPrepaidMultiOrder?: boolean;
  hasManualDiscounts?: boolean;
  hasDiscountCodes?: boolean;
  cancelMitigationDiscountAlreadyApplied?: boolean;
  shouldExpireContract?: boolean;
}

const DEFAULT_CANCEL_MITIGATION = [
  {
    code: CancelReasons.BUDGET,
    label: `cancelModal.${CancelReasons.BUDGET}`,
    flow: [],
  },
  {
    code: CancelReasons.COMPETITOR,
    label: `cancelModal.${CancelReasons.COMPETITOR}`,
    flow: [],
  },
  {
    code: CancelReasons.MOVING,
    label: `cancelModal.${CancelReasons.MOVING}`,
    flow: ['updateAddress'],
  },
  {
    code: CancelReasons.NOT_ENJOY,
    label: `cancelModal.${CancelReasons.NOT_ENJOY}`,
    flow: [],
  },
  {
    code: CancelReasons.NO_NEED,
    label: `cancelModal.${CancelReasons.NO_NEED}`,
    flow: ['pause'],
  },
  {
    code: CancelReasons.TOO_MUCH,
    label: `cancelModal.${CancelReasons.TOO_MUCH}`,
    flow: ['frequency'],
  },
  {
    code: CancelReasons.TRAVELLING,
    label: `cancelModal.${CancelReasons.TRAVELLING}`,
    flow: ['skip'],
  },
  {
    code: CancelReasons.OTHER,
    label: `cancelModal.${CancelReasons.OTHER}`,
    flow: [],
  },
];

const FLOW_MODAL_MAP = {
  // reasons: [<ReasonSelectorModal reasonsToMap={DEFAULT_CANCEL_MITIGATION} />],
  frequency: [<FrequencyEditorStep1Modal />, <FrequencyEditorStep2Modal />],
  updateAddress: [
    <UpdateAddressPromptModal />,
    <UpdateAddressFormModal />,
    <UpdateAddressConfirmModal />,
  ],
  showDiscount: [<DiscountAvailableModal />],
  pause: [<PausePromptModal />],
  skip: [<SkipPromptModal />, <RescheduleModal />],
  reschedule: [<RescheduleModal />],
};

const getConfigWithFlows = (
  cancelMitigationSettings,
  enablePrepaidCancelMitigation: boolean,
  getSteps: (stepList?: string[]) => JSX.Element[],
  isPrepaidMultiOrder: boolean | undefined,
  shouldExpireContract: boolean,
) => {
  const reasonsToMap: Reason[] =
    cancelMitigationSettings?.reasons || DEFAULT_CANCEL_MITIGATION;

  if (isPrepaidMultiOrder) {
    return {
      firstStep: enablePrepaidCancelMitigation ? (
        <ReasonSelectorModal reasonsToMap={reasonsToMap} />
      ) : (
        <ConfirmCancelPrepaidMultiOrderModal
          shouldExpireContract={shouldExpireContract}
        />
      ),
      flows: reasonsToMap
        .map(({ code, flow }) => ({ [code]: getSteps(flow) }))
        .reduce((acc, item) => ({ ...acc, ...item }), {}),
    };
  }
  return {
    firstStep: <ReasonSelectorModal reasonsToMap={reasonsToMap} />,
    flows: reasonsToMap
      .map(({ code, flow }) => ({ [code]: getSteps(flow) }))
      .reduce((acc, item) => ({ ...acc, ...item }), {}),
  };
};

const createStepFilterFunction =
  (
    isBatchedV2 = false,
    ignorePauseModal = false,
    ignoreFrequencyModal = false,
    ignoreSkipNextModal = false,
    ignoreRescheduleModal = false,
    ignoreDiscountModal = false,
  ) =>
  (step) => {
    let valid = true;
    if (
      isBatchedV2 &&
      [
        PausePromptModal,
        FrequencyEditorStep1Modal,
        FrequencyEditorStep2Modal,
        SkipPromptModal,
        RescheduleModal,
      ].includes(step.type)
    ) {
      valid = false;
    } else if (ignorePauseModal && step.type === PausePromptModal) {
      valid = false;
    } else if (
      ignoreFrequencyModal &&
      [FrequencyEditorStep1Modal, FrequencyEditorStep2Modal].includes(step.type)
    ) {
      valid = false;
    } else if (ignoreSkipNextModal && step.type === SkipPromptModal) {
      valid = false;
    } else if (ignoreRescheduleModal && step.type === RescheduleModal) {
      valid = false;
    } else if (ignoreDiscountModal && step.type === DiscountAvailableModal) {
      valid = false;
    }
    return valid;
  };

const CancelModal: React.FC<Props> = ({
  isBatchedV2,
  isPrepaidMultiOrder,
  hasManualDiscounts,
  hasDiscountCodes,
  cancelMitigationDiscountAlreadyApplied,
  shouldExpireContract,
}) => {
  const { isOpen, onClose } = useModalManagerContext();
  const { customerPortalSettings } = useGlobalContext();

  const isCancelMitigationDiscountAllowed =
    customerPortalSettings?.cancelMitigationDiscount?.enabled &&
    customerPortalSettings?.cancelMitigationDiscount?.discountValue &&
    !hasManualDiscounts &&
    !hasDiscountCodes &&
    !cancelMitigationDiscountAlreadyApplied &&
    !isPrepaidMultiOrder;

  const cancelMitigationSettings =
    customerPortalSettings?.cancelMitigationSettings;

  const enablePrepaidCancelMitigation =
    !!customerPortalSettings?.enablePrepaidCancelMitigation;

  const ignorePauseModal = customerPortalSettings?.hidePause;
  const ignoreSkipNextModal =
    customerPortalSettings?.hideSkipNext &&
    customerPortalSettings?.hideEditNextOrderDate;
  const ignoreRescheduleModal = customerPortalSettings?.hideReschedule;
  const ignoreFrequencyModal =
    customerPortalSettings?.hideEditFrequency ||
    customerPortalSettings?.hideCustomFrequency;

  const getFilteredSteps = (stepList: string[] = []): JSX.Element[] => {
    const steps = stepList.flatMap((step) => FLOW_MODAL_MAP[step]);

    // Add discount modal only if it's globally set, and we don't already include it
    const filteredSteps = steps.filter(
      createStepFilterFunction(
        isBatchedV2,
        ignorePauseModal,
        ignoreFrequencyModal,
        ignoreSkipNextModal,
        ignoreRescheduleModal,
        !isCancelMitigationDiscountAllowed,
      ),
    );
    const isEligibleForPrepaidCancellation =
      isPrepaidMultiOrder && !shouldExpireContract;

    if (customerPortalSettings?.cancelMitigationSettings) {
      return [
        ...filteredSteps,
        <ConfirmCancellationModal
          isEligibleForPrepaidCancellation={isEligibleForPrepaidCancellation}
          shouldExpireContract={shouldExpireContract}
        />,
        isEligibleForPrepaidCancellation ? (
          <ConfirmPrepaidCancellationModal />
        ) : null,
      ].filter(Boolean);
    } else {
      if (isCancelMitigationDiscountAllowed) {
        return [
          ...filteredSteps,
          <DiscountAvailableModal />,
          <ConfirmCancellationModal
            isEligibleForPrepaidCancellation={isEligibleForPrepaidCancellation}
            shouldExpireContract={shouldExpireContract}
          />,
          isEligibleForPrepaidCancellation ? (
            <ConfirmPrepaidCancellationModal />
          ) : null,
        ].filter(Boolean);
      }
    }

    return [
      ...filteredSteps,
      <ConfirmCancellationModal
        isEligibleForPrepaidCancellation={isEligibleForPrepaidCancellation}
        shouldExpireContract={shouldExpireContract}
      />,
      isEligibleForPrepaidCancellation ? (
        <ConfirmPrepaidCancellationModal />
      ) : null,
    ].filter(Boolean);
  };

  const configWithFlows = getConfigWithFlows(
    cancelMitigationSettings,
    enablePrepaidCancelMitigation,
    getFilteredSteps,
    isPrepaidMultiOrder,
    shouldExpireContract,
  );

  return (
    <ModalManager
      type="cancel-mitigation"
      config={configWithFlows}
      isOpen={isOpen}
      onClose={onClose}
    />
  );
};

export default CancelModal;
